import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { command } from '../api/client.js';
import { invalidate, useCommand } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import { Button, Field, Form, Notice, Textarea } from '../ui/primitives.jsx';
import { ErrorView } from '../ui/states.jsx';
import { readProposal } from './proposal-presentation.js';

// Inbox와 Task 상세는 같은 판단 화면과 같은 명령을 쓴다. 승격 규칙은 서버에만 있다.
export function ProposalDecision({ proposal, task, spaceId, mine, name, expanded = true,
  setExpanded, archived, openProposalCount = 1, onAccepted, evidence }) {
  const session = useSession();
  const [mode, setMode] = useState(null);
  const [rationale, setRationale] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const rationaleInput = useRef(null);
  const acceptTrigger = useRef(null);
  const declineTrigger = useRef(null);
  const resultHeading = useRef(null);
  const lastMode = useRef(null);
  const lock = useRef(false);
  const intent = useRef(null);
  const response = useCommand((params, idempotencyKey) =>
    command(params.operation, params.body, { nodeId: session.nodeId, idempotencyKey }),
  { invalidates: [`space:${spaceId}:task`, `space:${spaceId}:tasks`, `space:${spaceId}:inbox`, `space:${spaceId}:conversation-activity`] });
  const uncertain = response.error && !(response.error.status >= 400 && response.error.status < 500 && response.error.code);
  const blocked = response.error?.status === 403 || response.error?.code === 'proposal_base_stale';
  const view = readProposal(proposal);
  const stale = proposal.baseVersionNo !== task.currentVersionNo;
  const responded = proposal.responses.some((response) => response.actorNodeId === mine);
  const canRespond = !archived && !stale && !responded && !submitted && !blocked
    && ['owner', 'participant'].includes(task.viewerRole);
  const canAccept = canRespond && view && session.channel !== 'bridge'
    && ['human', 'hybrid'].includes(session.node.kind);
  useLayoutEffect(() => {
    if (submitted || blocked) resultHeading.current?.focus();
    else if (mode) rationaleInput.current?.focus();
    else if (lastMode.current) (lastMode.current === 'accept' ? acceptTrigger : declineTrigger).current?.focus();
    lastMode.current = mode;
  }, [mode, submitted, blocked]);
  const submit = async () => {
    if (lock.current || !canRespond || (mode === 'accept' && !canAccept)
      || !mode || (mode === 'decline' && !rationale.trim())) return;
    lock.current = true;
    try {
      if (!intent.current) intent.current = { operation: `proposal_${mode}`,
        body: { spaceId, proposalId: proposal.proposalId, rationale: rationale.trim() || undefined } };
      const result = await response.run(intent.current);
      if (result.ok || (result.error?.status >= 400 && result.error?.status < 500 && result.error?.code)) intent.current = null;
      if (result.ok) {
        setSubmitted(true);
        if (mode === 'accept') onAccepted?.(task.currentVersionNo, openProposalCount, proposal.proposalId);
      }
    } finally { lock.current = false; }
  };
  return <article class="promise proposal-decision">
    <div class="row row--between">
      <p ref={resultHeading} tabIndex={-1} class="t-body-strong">제안 p{proposal.number} · {name(proposal.submittedByNodeId)}</p>
      {openProposalCount > 1 && setExpanded ? <Button size="sm" onClick={() => setExpanded(expanded ? null : proposal.proposalId)}>{expanded ? '접기' : '펼치기'}</Button> : null}
    </div>
    <p class="t-meta">{proposal.baseVersionNo === null ? '첫 공동 이해 제안' : `기준 공동 이해 v${proposal.baseVersionNo}`}</p>
    {expanded ? <div class="stack task-proposal">
      {view ? <>
        <p class="t-body">{view.purpose}</p>
        <section class="proposal-change" aria-label="바뀌는 내용">
          <h3 class="t-body-strong">바뀌는 내용</h3>
          <ChangeList label="추가할 사실" items={view.added} />
          <ChangeList label="제외할 사실" items={view.removed} />
          {!view.added.length && !view.removed.length ? <p class="t-meta">확정 사실은 바뀌지 않습니다.</p> : null}
          <ChangeList label="추가할 열린 질문" items={proposal.resolvedChange.openQuestions.added} />
          <ChangeList label="제외할 열린 질문" items={proposal.resolvedChange.openQuestions.removed} />
          <p class="t-meta">근거 {view.counts.referenceIds}건 · 추가 {proposal.resolvedChange.referenceIds.added.length} · 제외 {proposal.resolvedChange.referenceIds.removed.length}</p>
        </section>
        <details><summary class="t-meta">확정 후 전체 내용 · 사실 {view.counts.confirmed} · 열린 질문 {view.counts.openQuestions}</summary>
          <div class="stack"><ChangeList label="확정 사실" items={view.confirmed} /><ChangeList label="열린 질문" items={view.openQuestions} /></div>
        </details>
        {evidence}
      </> : <Notice tone="waiting" title="변경 내용을 읽을 수 없어 확정할 수 없습니다." />}
      {stale ? <Notice tone="waiting" title="기준이 바뀐 제안입니다. 최신 공동 이해를 확인하고 새 제안을 요청하세요." /> : null}
      {proposal.responses.map((item) => <p class="t-meta" key={item.responseId}>
        {name(item.actorNodeId)} · {item.response === 'accept' ? '변경 확정에 동의' : '반대 기록'}
        {' · '}{({ human_direct: '사람 직접', delegated_ai: '위임 AI', autonomous_ai: 'AI' })[item.actorProvenance] ?? '출처 미확인'}
        {item.rationale ? ` · ${item.rationale}` : ''}
      </p>)}
      {proposal.responses.some((item) => item.response === 'decline') ? <p class="t-meta">반대는 기록으로 남으며 제안을 닫지 않습니다.</p> : null}
      {canRespond && !mode ? <div class="row">
        {canAccept ? <Button ref={acceptTrigger} disabled={response.blocked} size="sm" tone="human" onClick={() => setMode('accept')}>변경 확정</Button> : null}
        <Button ref={declineTrigger} disabled={response.blocked} size="sm" onClick={() => setMode('decline')}>반대 의견</Button>
      </div> : null}
      {canRespond && mode ? <Form class="stack" busy={response.pending} onSubmit={submit}>
        <p class="t-meta">{mode === 'accept' ? '이 변경에 동의한다는 판단을 남깁니다. 공동 이해는 서버의 종결 정책이 충족될 때 기록됩니다.' : '반대 이유를 남깁니다. 제안 자체를 닫는 동작은 아닙니다.'}</p>
        <Field label={mode === 'accept' ? '판단 근거 (선택)' : '반대 근거'} htmlFor={`decision-${proposal.proposalId}`} required={mode === 'decline'}>
          <Textarea ref={rationaleInput} id={`decision-${proposal.proposalId}`} value={rationale} disabled={response.pending || uncertain}
            onInput={(event) => setRationale(event.currentTarget.value)} />
        </Field>
        {response.error ? <ErrorView error={response.error} /> : null}
        {uncertain ? <p role="status" class="t-meta">처리 결과를 아직 확인하지 못했습니다. 내용을 바꾸지 않고 같은 판단으로 다시 확인합니다.</p> : null}
        <div class="row">
          <Button kind="submit" size="sm" tone={mode === 'accept' ? 'human' : 'danger'} busy={response.pending}
            disabled={mode === 'decline' && !rationale.trim()}>{uncertain ? '같은 판단 다시 확인' : mode === 'accept' ? '이 변경에 동의' : '반대 기록'}</Button>
          <Button size="sm" disabled={response.pending || uncertain}
            onClick={() => { setMode(null); response.reset(); }}>취소</Button>
        </div>
      </Form> : null}
      {blocked ? <><ErrorView error={response.error} /><Button size="sm" onClick={() => invalidate(`space:${spaceId}:task`)}>최신 상태 확인</Button></> : null}
      {submitted ? <p role="status" class="t-meta">판단을 기록했습니다. 최신 공동 이해를 확인하고 있습니다.</p> : null}
    </div> : null}
  </article>;
}

function ChangeList({ label, items = [] }) {
  return items.length ? <div><p class="t-meta">{label}</p><ul class="task-facts">{items.map((item) =>
    <li key={item.id ?? item}>{item.text ?? item}</li>)}</ul></div> : null;
}
